import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from "axios";
import {
  FaListUl, FaPlus, FaSearch, FaTrash, FaPencilAlt, FaTimes,
  FaSitemap, FaSyncAlt, FaLayerGroup, FaFileExcel,
} from 'react-icons/fa';
import { getAllDropDowns, deleteDropDown, createDropDown, editDropDown } from '../Redux/Store/API/DropDownApi';
import {
  LINKED_GROUPS, fetchLinkedDropdown, linkedError,
  addLinkedParent, editLinkedParent, deleteLinkedParent,
  addLinkedChild, editLinkedChild, deleteLinkedChild,
} from '../Services/linkedDropdowns';
import { setClinicProfile, getJwtToken } from "../Redux/AdminReducer/action";
import exportToExcel from '../UtilityHookFunctions/exportToExcel';
import { Button, Input, Badge, ConfirmDialog } from '../Components/UI';

export let BaseUrl

if (window.location.protocol === 'http:') {
    BaseUrl = `http://localhost:5001/`
}
else {
    BaseUrl = `https://${window.location.host}/api/`
}

// "status_of_enquiry" -> "Status Of Enquiry"
const prettyName = (key = '') =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// How many patients hold `value` in the patient field(s) tied to list `name`.
// Resolves to { count, tracked } — tracked is false for lists that are not
// stored on the patient record (e.g. potency), where the count means nothing.
const fetchUsage = async (name, value) => {
  const res = await axios.post(
    `${BaseUrl}dropdown/usage`,
    { name, value },
    { headers: { Authorization: getJwtToken() } }
  );
  return { count: res?.data?.count || 0, tracked: !!res?.data?.tracked };
};

const patientsLabel = (count) => `${count} patient${count === 1 ? '' : 's'}`;

/* ---------------- Shared building blocks ---------------- */

const Panel = ({ title, subtitle, icon, action, children, className = '' }) => (
  <div className={`flex flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm ${className}`}>
    <div className="flex items-center justify-between gap-3 border-b border-slate-100 bg-slate-50/70 px-4 py-3">
      <div className="min-w-0">
        <h2 className="flex items-center gap-2 truncate text-sm font-semibold text-slate-800">
          {icon}
          {title}
        </h2>
        {subtitle && <p className="mt-0.5 truncate text-xs text-slate-500">{subtitle}</p>}
      </div>
      {action}
    </div>
    {children}
  </div>
);

const EmptyState = ({ icon, title, hint }) => (
  <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-12 text-center">
    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-400">
      {icon}
    </div>
    <p className="text-sm font-medium text-slate-600">{title}</p>
    {hint && <p className="max-w-xs text-xs text-slate-400">{hint}</p>}
  </div>
);

const ListSkeleton = () => (
  <div className="space-y-2 p-4">
    {[...Array(7)].map((_, i) => (
      <div key={i} className="h-9 animate-pulse rounded-lg bg-slate-100" />
    ))}
  </div>
);

/* ---------------- Tab 1: dropdown lists ---------------- */

const DropDownOption = () => {
  const [dropdownData, setDropdownData] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const [keys, setKeys] = useState('');
  const [values, setValues] = useState('');
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editingValue, setEditingValue] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [renaming, setRenaming] = useState(false);
  // Usage of the value being deleted / renamed: null while unknown.
  const [deleteUsage, setDeleteUsage] = useState(null);
  const [editUsage, setEditUsage] = useState(null);
  const [updatePatients, setUpdatePatients] = useState(true);
  const dispatch = useDispatch()

  const { getallDropDowns, loading } = useSelector((state) => state.dropDown);

  useEffect(() => {
   dispatch(getAllDropDowns())
  }, [dispatch]);

  useEffect(() => {
    dispatch(setClinicProfile());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (getallDropDowns?.length > 0) {
      const apiData = getallDropDowns[0];

      // Only the list fields — skips Mongo bookkeeping keys such as _id / __v.
      const formattedData = Object.keys(apiData)
        .filter((key) => Array.isArray(apiData[key]))
        .map((key) => ({ name: key, value: apiData[key] }));

      if (keys) {
        let keysdata = formattedData.find(d => d.name === keys);
        setSelectedValues(keysdata?.value || []);
      }

      setDropdownData(formattedData);
    }
  }, [getallDropDowns, keys]);

  const filteredData = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return dropdownData;
    return dropdownData.filter((item) => prettyName(item.name).toLowerCase().includes(q));
  }, [dropdownData, search]);

  const handleNameClick = (values) => {
    setSelectedValues(values);
  };

  const askDelete = async (value) => {
    setDeleteTarget(value);
    setDeleteUsage({ loading: true });
    try {
      setDeleteUsage(await fetchUsage(keys, value));
    } catch (error) {
      setDeleteUsage({ failed: true });
    }
  };

  const closeDelete = () => {
    setDeleteTarget(null);
    setDeleteUsage(null);
  };

  const deleteMessage = (() => {
    const base = `Remove "${deleteTarget}" from ${prettyName(keys)}? It will no longer be selectable in forms.`;
    if (!deleteUsage || deleteUsage.loading) return `${base} Checking how many patients use it…`;
    if (deleteUsage.failed) return `${base} Could not check how many patients use it.`;
    if (!deleteUsage.tracked || deleteUsage.count === 0) return `${base} No patients currently use this value.`;
    return `${base} ${patientsLabel(deleteUsage.count)} still ${deleteUsage.count === 1 ? 'has' : 'have'} this value; their records keep it, but it will show under an option that no longer exists. Delete anyway?`;
  })();

  // Handle delete operation
  const handleDelete = async (value) => {
    setDeleting(true);
    try {
      const res = await dispatch(deleteDropDown({ name: keys, value }));
      if (res.error || res.payload?.response === false) {
        throw new Error(res.payload?.message || 'Could not remove the option');
      }
      await dispatch(getAllDropDowns());
      toast.success(`Removed "${value}"`);
      closeDelete();
    } catch (error) {
      toast.error(error.message || 'Could not remove the option');
    } finally {
      setDeleting(false);
    }
  };

  const startEditing = async (value) => {
    setEditingValue(value);
    setEditingText(String(value));
    setUpdatePatients(true);
    setEditUsage({ loading: true });
    try {
      const usage = await fetchUsage(keys, value);
      setEditUsage((current) => (current ? usage : current));
    } catch (error) {
      setEditUsage(null);
    }
  };

  const cancelEditing = () => {
    setEditingValue(null);
    setEditingText('');
    setEditUsage(null);
  };

  // Handle rename operation
  const handleRename = async () => {
    const next = editingText.trim();
    if (!next) return toast.info('Enter a value');
    if (next === editingValue) return cancelEditing();
    if (selectedValues?.some((v) => String(v).toLowerCase() === next.toLowerCase())) {
      return toast.info(`"${next}" is already in this list`);
    }

    setRenaming(true);
    try {
      const syncPatients = !!(editUsage?.tracked && editUsage?.count > 0 && updatePatients);
      const res = await dispatch(editDropDown({ name: keys, old_value: editingValue, new_value: next, update_patients: syncPatients }));
      if (res.error) throw new Error(res.payload?.message || 'Could not rename the option');
      await dispatch(getAllDropDowns());
      const updated = res.payload?.patientsUpdated || 0;
      toast.success(syncPatients ? `Renamed to "${next}" and updated ${patientsLabel(updated)}` : `Renamed to "${next}"`);
      cancelEditing();
    } catch (error) {
      toast.error(error.message || 'Could not rename the option');
    } finally {
      setRenaming(false);
    }
  };

  // Every option of every list, one row each.
  const handleExport = () => {
    const rows = dropdownData.flatMap((item) =>
      (item.value || []).map((option, index) => ({
        list: prettyName(item.name),
        key: item.name,
        position: index + 1,
        option,
      }))
    );
    if (!rows.length) return toast.info('There are no options to export');
    exportToExcel(
      rows,
      'dropdown-options',
      [
        { key: 'list', label: 'List' },
        { key: 'key', label: 'Key' },
        { key: 'position', label: '#' },
        { key: 'option', label: 'Option' },
      ],
      'Dropdown Options'
    );
  };

  // Handle add operation
  const handleAdd = async () => {
    const value = values.trim();
    if (!keys) return toast.info('Pick a list on the left first');
    if (!value) return toast.info('Enter a value to add');
    if (selectedValues?.some((v) => String(v).toLowerCase() === value.toLowerCase())) {
      return toast.info(`"${value}" is already in this list`);
    }

    setSaving(true);
    try {
      const res = await dispatch(createDropDown({ name: keys, value }));
      if (res.error) throw new Error(res.payload?.message || 'Could not add the option');
      setValues('');
      await dispatch(getAllDropDowns());
      toast.success(`Added "${value}" to ${prettyName(keys)}`);
    } catch (error) {
      toast.error(error.message || 'Could not add the option');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
      {/* ---- Lists ---- */}
      <Panel
        title="Lists"
        subtitle={`${dropdownData.length} configurable dropdowns`}
        icon={<FaListUl className="h-3.5 w-3.5 text-sky-600" />}
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={!dropdownData.length}
            icon={<FaFileExcel className="h-3.5 w-3.5" />}
            className="shrink-0"
          >
            Export to Excel
          </Button>
        }
      >
        <div className="border-b border-slate-100 p-3">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search lists..."
            leadingIcon={<FaSearch className="h-3.5 w-3.5" />}
          />
        </div>

        {loading && !dropdownData.length ? (
          <ListSkeleton />
        ) : filteredData.length === 0 ? (
          <EmptyState icon={<FaListUl className="h-4 w-4" />} title="No lists match your search" />
        ) : (
          <ul className="max-h-[560px] space-y-1 overflow-y-auto p-2">
            {filteredData.map((item) => {
              const isActive = item.name === keys;
              return (
                <li key={item.name}>
                  <button
                    onClick={() => {
                      cancelEditing();
                      setKeys(item.name);
                      handleNameClick(item.value);
                    }}
                    className={`flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left text-sm transition ${
                      isActive
                        ? 'bg-sky-600 font-semibold text-white shadow-sm shadow-sky-600/25'
                        : 'text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    <span className="truncate">{prettyName(item.name)}</span>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {item.value?.length || 0}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>

      {/* ---- Options of the selected list ---- */}
      <Panel
        title={keys ? prettyName(keys) : 'Options'}
        subtitle={keys ? 'Values offered in this dropdown across the app' : 'Select a list to see its values'}
        icon={<FaLayerGroup className="h-3.5 w-3.5 text-sky-600" />}
        action={keys ? <Badge variant="primary">{selectedValues?.length || 0} options</Badge> : null}
        className="min-h-[420px]"
      >
        {!keys ? (
          <EmptyState
            icon={<FaLayerGroup className="h-4 w-4" />}
            title="No list selected"
            hint="Pick a dropdown on the left to add or remove its options."
          />
        ) : (
          <>
            <div className="flex flex-col gap-2 border-b border-slate-100 bg-slate-50/50 p-3 sm:flex-row">
              <Input
                value={values}
                onChange={(e) => setValues(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                placeholder={`Add a new option to ${prettyName(keys)}`}
              />
              <Button
                onClick={handleAdd}
                isLoading={saving}
                icon={<FaPlus className="h-3 w-3" />}
                className="shrink-0"
              >
                Add
              </Button>
            </div>

            {selectedValues?.length === 0 ? (
              <EmptyState
                icon={<FaPlus className="h-4 w-4" />}
                title="This list is empty"
                hint="Add the first option using the field above."
              />
            ) : (
              <ul className="max-h-[480px] divide-y divide-slate-100 overflow-y-auto">
                {selectedValues.map((value, index) => (
                  <li
                    key={`${value}-${index}`}
                    className="group flex items-center justify-between gap-3 px-4 py-2.5 transition hover:bg-slate-50"
                  >
                    {editingValue === value ? (
                      <div className="flex w-full flex-col gap-2">
                        <div className="flex items-center gap-3">
                          <Input
                            value={editingText}
                            autoFocus
                            onChange={(e) => setEditingText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleRename();
                              if (e.key === 'Escape') cancelEditing();
                            }}
                          />
                          <div className="flex shrink-0 gap-2">
                            <Button size="sm" onClick={handleRename} isLoading={renaming}>Save</Button>
                            <Button size="sm" variant="outline" onClick={cancelEditing}>Cancel</Button>
                          </div>
                        </div>
                        {editUsage?.loading && (
                          <p className="text-xs text-slate-400">Checking patients using this value…</p>
                        )}
                        {editUsage?.tracked && editUsage.count > 0 && (
                          <label className="flex items-center gap-2 text-xs text-slate-600">
                            <input
                              type="checkbox"
                              checked={updatePatients}
                              onChange={(e) => setUpdatePatients(e.target.checked)}
                              className="h-3.5 w-3.5 rounded border-slate-300"
                            />
                            Also update {patientsLabel(editUsage.count)} that use "{value}" so graphs stay in sync
                          </label>
                        )}
                        {editUsage?.tracked && editUsage.count === 0 && (
                          <p className="text-xs text-slate-400">No patients use this value yet.</p>
                        )}
                      </div>
                    ) : (
                      <>
                        <span className="truncate text-sm text-slate-700">{value}</span>
                        <div className="flex shrink-0 gap-1 opacity-0 transition focus-within:opacity-100 group-hover:opacity-100">
                          <button
                            onClick={() => startEditing(value)}
                            title="Rename option"
                            className="rounded-lg p-2 text-slate-400 transition hover:bg-sky-50 hover:text-sky-600"
                          >
                            <FaPencilAlt className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => askDelete(value)}
                            title="Delete option"
                            className="rounded-lg p-2 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
                          >
                            <FaTrash className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </Panel>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={closeDelete}
        onConfirm={() => handleDelete(deleteTarget)}
        isLoading={deleting || !!deleteUsage?.loading}
        title="Delete option"
        confirmText={deleteUsage?.count > 0 ? 'Delete anyway' : 'Delete'}
        message={deleteMessage}
      />
    </div>
  );
};

/* ---------------- Tab 2: services & sub-services ---------------- */

const Services = () => {
    const [dropdownData, setDropdownData] = useState([]);
    const [selectedValues, setSelectedValues] = useState({});
    const [mainService, setMainService] = useState('');
    const [subService, setSubService] = useState('');
    const [editingService, setEditingService] = useState(null);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [savingMain, setSavingMain] = useState(false);
    const [savingSub, setSavingSub] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'main' | 'sub', value }
    const [deleting, setDeleting] = useState(false);

    const fetchMainServices = async () => {
        try {
            const res = await axios.get(`${BaseUrl}subAilments/main-services`);
            if(selectedValues?.main_service){
              let selecteddata = res?.data?.data?.filter(d=>d?.main_service===selectedValues?.main_service)?.[0]
              setSelectedValues(selecteddata || {})
            }
            setDropdownData(res.data.data);
        } catch (error) {
            console.error('Error fetching main services:', error);
            toast.error('Could not load services');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMainServices();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const filteredServices = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return dropdownData;
        return dropdownData.filter((item) => item?.main_service?.toLowerCase().includes(q));
    }, [dropdownData, search]);

    const cancelEditing = () => {
        setEditingService(null);
        setMainService('');
    };

    const handleAddMainService = async () => {
        if (!mainService.trim()) return toast.info('Enter a service name');
        setSavingMain(true);
        try {
            await axios.post(`${BaseUrl}subAilments/add-main-service`, { main_service: mainService.trim() });
            toast.success('Main service added successfully!');
            setMainService('');
            setEditingService(null);
            fetchMainServices();
        } catch (error) {
            console.error('Error adding main service:', error);
            toast.error('Could not add the service');
        } finally {
            setSavingMain(false);
        }
    };

    const handleAddSubService = async () => {
        if (!subService.trim()) return toast.info('Enter a sub-service name');
        setSavingSub(true);
        try {
            await axios.post(`${BaseUrl}subAilments/add-sub-service`, {
                main_service: selectedValues.main_service,
                sub_service: subService.trim(),
            });
            toast.success('Sub-service added successfully!');
            setSubService('');
            fetchMainServices();
        } catch (error) {
            console.error('Error adding sub-service:', error);
            toast.error('Could not add the sub-service');
        } finally {
            setSavingSub(false);
        }
    };

    const handleEditMainService = async () => {
      if (!editingService) return;
      if (!mainService.trim()) return toast.info('Enter a service name');

      setSavingMain(true);
      try {
          await axios.put(`${BaseUrl}subAilments/edit-main-service`, {
              old_main_service: editingService.main_service,
              new_main_service: mainService.trim(),
          });
          toast.success('Main service updated successfully!');
          setMainService('');
          setEditingService(null);
          setSelectedValues({});
          fetchMainServices();
      } catch (error) {
          console.error('Error editing main service:', error);
          toast.error('Could not update the service');
      } finally {
          setSavingMain(false);
      }
  };

  const handleDeleteMainService = async (name) => {
    if (!name) return;

    setDeleting(true);
    try {
        await axios.delete(`${BaseUrl}subAilments/delete-main-service`, {
            data: { main_service: name },
        });
        toast.success('Main service and its sub-services deleted successfully!');
        setSelectedValues({});
        setDeleteTarget(null);
        fetchMainServices();
    } catch (error) {
        console.error('Error deleting main service:', error);
        toast.error('Could not delete the service');
    } finally {
        setDeleting(false);
    }
};

    const handleDeleteSubService = async (sub) => {
        setDeleting(true);
        try {
            await axios.post(`${BaseUrl}subAilments/delete-sub-service`, {
                main_service: selectedValues.main_service,
                sub_service: sub,
            });
            toast.success('Sub-service deleted successfully!');
            setDeleteTarget(null);
            fetchMainServices();
        } catch (error) {
            console.error('Error deleting sub-service:', error);
            toast.error('Could not delete the sub-service');
        } finally {
            setDeleting(false);
        }
    };

    return (
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)]">
          {/* ---- Main services ---- */}
          <Panel
              title="Main services"
              subtitle={`${dropdownData.length} services offered`}
              icon={<FaSitemap className="h-3.5 w-3.5 text-sky-600" />}
          >
              <div className="space-y-2 border-b border-slate-100 bg-slate-50/50 p-3">
                  <div className="flex flex-col gap-2 sm:flex-row">
                      <Input
                          value={mainService}
                          onChange={(e) => setMainService(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && (editingService ? handleEditMainService() : handleAddMainService())}
                          placeholder={editingService ? 'Rename this service' : 'Add a main service'}
                      />
                      <div className="flex shrink-0 gap-2">
                          <Button
                              onClick={editingService ? handleEditMainService : handleAddMainService}
                              isLoading={savingMain}
                              variant={editingService ? 'primary' : 'success'}
                              icon={editingService ? <FaPencilAlt className="h-3 w-3" /> : <FaPlus className="h-3 w-3" />}
                          >
                              {editingService ? 'Update' : 'Add'}
                          </Button>
                          {editingService && (
                              <Button variant="outline" onClick={cancelEditing} icon={<FaTimes className="h-3 w-3" />}>
                                  Cancel
                              </Button>
                          )}
                      </div>
                  </div>
                  {editingService && (
                      <p className="text-xs text-slate-500">
                          Renaming <span className="font-semibold text-slate-700">{editingService.main_service}</span>
                      </p>
                  )}
                  <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search services..."
                      leadingIcon={<FaSearch className="h-3.5 w-3.5" />}
                  />
              </div>

              {loading ? (
                  <ListSkeleton />
              ) : filteredServices.length === 0 ? (
                  <EmptyState
                      icon={<FaSitemap className="h-4 w-4" />}
                      title={search ? 'No services match your search' : 'No services yet'}
                      hint={search ? undefined : 'Add your first main service above.'}
                  />
              ) : (
                  <ul className="max-h-[520px] space-y-1 overflow-y-auto p-2">
                      {filteredServices.map((item, index) => {
                          const isActive = selectedValues?.main_service === item.main_service;
                          return (
                              <li key={item._id || index}>
                                  <div
                                      onClick={() => {
                                          if (editingService) cancelEditing();
                                          setSelectedValues(item);
                                      }}
                                      className={`group flex cursor-pointer items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-sm transition ${
                                          isActive
                                              ? 'bg-sky-600 font-semibold text-white shadow-sm shadow-sky-600/25'
                                              : 'text-slate-700 hover:bg-slate-100'
                                      }`}
                                  >
                                      <span className="min-w-0 flex-1 truncate">{item.main_service}</span>
                                      <span
                                          className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                                              isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                                          }`}
                                      >
                                          {item.sub_services?.length || 0}
                                      </span>
                                      <div className="flex shrink-0 gap-1 opacity-0 transition group-hover:opacity-100">
                                          <button
                                              onClick={(e) => {
                                                  e.stopPropagation(); // Prevent triggering the selection logic
                                                  setEditingService(item);
                                                  setMainService(item.main_service);
                                                  setSelectedValues(item);
                                              }}
                                              title="Rename service"
                                              className={`rounded-lg p-1.5 transition ${
                                                  isActive ? 'text-white hover:bg-white/20' : 'text-slate-400 hover:bg-sky-50 hover:text-sky-600'
                                              }`}
                                          >
                                              <FaPencilAlt className="h-3 w-3" />
                                          </button>
                                          <button
                                              onClick={(e) => {
                                                  e.stopPropagation(); // Prevent triggering the selection logic
                                                  setDeleteTarget({ type: 'main', value: item.main_service });
                                              }}
                                              title="Delete service"
                                              className={`rounded-lg p-1.5 transition ${
                                                  isActive ? 'text-white hover:bg-white/20' : 'text-slate-400 hover:bg-rose-50 hover:text-rose-600'
                                              }`}
                                          >
                                              <FaTrash className="h-3 w-3" />
                                          </button>
                                      </div>
                                  </div>
                              </li>
                          );
                      })}
                  </ul>
              )}
          </Panel>

          {/* ---- Sub-services ---- */}
          <Panel
              title={selectedValues?.main_service || 'Sub-services'}
              subtitle={selectedValues?.main_service ? 'Sub-services under this main service' : 'Select a main service to manage its sub-services'}
              icon={<FaLayerGroup className="h-3.5 w-3.5 text-sky-600" />}
              action={
                  selectedValues?.main_service
                      ? <Badge variant="primary">{selectedValues?.sub_services?.length || 0} sub-services</Badge>
                      : null
              }
              className="min-h-[420px]"
          >
              {!selectedValues?.main_service ? (
                  <EmptyState
                      icon={<FaSitemap className="h-4 w-4" />}
                      title="No service selected"
                      hint="Pick a main service on the left to add or remove its sub-services."
                  />
              ) : (
                  <>
                      <div className="flex flex-col gap-2 border-b border-slate-100 bg-slate-50/50 p-3 sm:flex-row">
                          <Input
                              value={subService}
                              onChange={(e) => setSubService(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleAddSubService()}
                              placeholder={`Add a sub-service to ${selectedValues.main_service}`}
                          />
                          <Button
                              onClick={handleAddSubService}
                              isLoading={savingSub}
                              icon={<FaPlus className="h-3 w-3" />}
                              className="shrink-0"
                          >
                              Add sub-service
                          </Button>
                      </div>

                      {!selectedValues?.sub_services?.length ? (
                          <EmptyState
                              icon={<FaPlus className="h-4 w-4" />}
                              title="No sub-services yet"
                              hint="Add the first sub-service using the field above."
                          />
                      ) : (
                          <ul className="max-h-[480px] divide-y divide-slate-100 overflow-y-auto">
                              {selectedValues.sub_services.map((sub, index) => (
                                  <li
                                      key={`${sub}-${index}`}
                                      className="group flex items-center justify-between gap-3 px-4 py-2.5 transition hover:bg-slate-50"
                                  >
                                      <span className="truncate text-sm text-slate-700">{sub}</span>
                                      <button
                                          onClick={() => setDeleteTarget({ type: 'sub', value: sub })}
                                          title="Delete sub-service"
                                          className="shrink-0 rounded-lg p-2 text-slate-400 opacity-0 transition hover:bg-rose-50 hover:text-rose-600 focus:opacity-100 group-hover:opacity-100"
                                      >
                                          <FaTrash className="h-3.5 w-3.5" />
                                      </button>
                                  </li>
                              ))}
                          </ul>
                      )}
                  </>
              )}
          </Panel>

          <ConfirmDialog
              isOpen={!!deleteTarget}
              onClose={() => setDeleteTarget(null)}
              onConfirm={() =>
                  deleteTarget?.type === 'main'
                      ? handleDeleteMainService(deleteTarget.value)
                      : handleDeleteSubService(deleteTarget.value)
              }
              isLoading={deleting}
              title={deleteTarget?.type === 'main' ? 'Delete main service' : 'Delete sub-service'}
              message={
                  deleteTarget?.type === 'main'
                      ? `Delete "${deleteTarget?.value}" and all of its sub-services? This cannot be undone.`
                      : `Remove "${deleteTarget?.value}" from ${selectedValues?.main_service}?`
              }
          />
      </div>
  );

};

/* ---------------- Tab 3: linked lists (parent -> child) ---------------- */

const LinkedLists = () => {
  const [group, setGroup] = useState(LINKED_GROUPS[0].value);
  const [rows, setRows] = useState([]);
  const [selectedParent, setSelectedParent] = useState('');
  const [parentInput, setParentInput] = useState('');
  const [childInput, setChildInput] = useState('');
  const [editingParent, setEditingParent] = useState(null);
  const [editingChild, setEditingChild] = useState(null);
  const [editingChildText, setEditingChildText] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingParent, setSavingParent] = useState(false);
  const [savingChild, setSavingChild] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'parent' | 'child', value }
  const [deleting, setDeleting] = useState(false);

  const meta = LINKED_GROUPS.find((g) => g.value === group) || LINKED_GROUPS[0];
  const selected = rows.find((row) => row.parent === selectedParent) || null;

  const load = async (nextGroup = group) => {
    setLoading(true);
    try {
      setRows(await fetchLinkedDropdown(nextGroup));
    } catch (error) {
      console.error('Error fetching linked dropdown:', error);
      toast.error('Could not load the list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(group);
  }, [group]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredParents = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => row.parent?.toLowerCase().includes(q));
  }, [rows, search]);

  const cancelParentEditing = () => {
    setEditingParent(null);
    setParentInput('');
  };

  const cancelChildEditing = () => {
    setEditingChild(null);
    setEditingChildText('');
  };

  const switchGroup = (next) => {
    setGroup(next);
    setSelectedParent('');
    setSearch('');
    cancelParentEditing();
    cancelChildEditing();
  };

  const handleAddParent = async () => {
    const value = parentInput.trim();
    if (!value) return toast.info(`Enter a ${meta.parentLabel.toLowerCase()}`);

    setSavingParent(true);
    try {
      await addLinkedParent(group, value);
      toast.success(`Added "${value}"`);
      setParentInput('');
      await load();
    } catch (error) {
      toast.error(linkedError(error, `Could not add the ${meta.parentLabel.toLowerCase()}`));
    } finally {
      setSavingParent(false);
    }
  };

  const handleEditParent = async () => {
    const value = parentInput.trim();
    if (!editingParent) return;
    if (!value) return toast.info(`Enter a ${meta.parentLabel.toLowerCase()}`);

    setSavingParent(true);
    try {
      await editLinkedParent(group, editingParent.parent, value);
      toast.success(`Renamed to "${value}"`);
      if (selectedParent === editingParent.parent) setSelectedParent(value);
      cancelParentEditing();
      await load();
    } catch (error) {
      toast.error(linkedError(error, 'Could not rename it'));
    } finally {
      setSavingParent(false);
    }
  };

  const handleDeleteParent = async (value) => {
    setDeleting(true);
    try {
      await deleteLinkedParent(group, value);
      toast.success(`Deleted "${value}"`);
      if (selectedParent === value) setSelectedParent('');
      setDeleteTarget(null);
      await load();
    } catch (error) {
      toast.error(linkedError(error, 'Could not delete it'));
    } finally {
      setDeleting(false);
    }
  };

  const handleAddChild = async () => {
    const value = childInput.trim();
    if (!value) return toast.info(`Enter a ${meta.childLabel.toLowerCase()}`);

    setSavingChild(true);
    try {
      await addLinkedChild(group, selectedParent, value);
      toast.success(`Added "${value}" to ${selectedParent}`);
      setChildInput('');
      await load();
    } catch (error) {
      toast.error(linkedError(error, `Could not add the ${meta.childLabel.toLowerCase()}`));
    } finally {
      setSavingChild(false);
    }
  };

  const handleEditChild = async () => {
    const value = editingChildText.trim();
    if (!editingChild) return;
    if (!value) return toast.info(`Enter a ${meta.childLabel.toLowerCase()}`);

    setSavingChild(true);
    try {
      await editLinkedChild(group, selectedParent, editingChild, value);
      toast.success(`Renamed to "${value}"`);
      cancelChildEditing();
      await load();
    } catch (error) {
      toast.error(linkedError(error, 'Could not rename it'));
    } finally {
      setSavingChild(false);
    }
  };

  const handleDeleteChild = async (value) => {
    setDeleting(true);
    try {
      await deleteLinkedChild(group, selectedParent, value);
      toast.success(`Removed "${value}"`);
      setDeleteTarget(null);
      await load();
    } catch (error) {
      toast.error(linkedError(error, 'Could not remove it'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200/80 bg-white p-3 shadow-sm">
        <span className="mr-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Pair</span>
        {LINKED_GROUPS.map((g) => (
          <button
            key={g.value}
            onClick={() => switchGroup(g.value)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
              group === g.value
                ? 'bg-sky-600 text-white shadow-sm shadow-sky-600/25'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            {g.label}
          </button>
        ))}
        <p className="w-full text-xs text-slate-500 sm:ml-auto sm:w-auto">
          Picking a {meta.parentLabel.toLowerCase()} in a form narrows its {meta.childLabel.toLowerCase()} to this list
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)]">
        {/* ---- Parents ---- */}
        <Panel
          title={meta.parentLabel}
          subtitle={`${rows.length} entries`}
          icon={<FaSitemap className="h-3.5 w-3.5 text-sky-600" />}
        >
          <div className="space-y-2 border-b border-slate-100 bg-slate-50/50 p-3">
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                value={parentInput}
                onChange={(e) => setParentInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') (editingParent ? handleEditParent() : handleAddParent());
                  if (e.key === 'Escape') cancelParentEditing();
                }}
                placeholder={editingParent ? `Rename this ${meta.parentLabel.toLowerCase()}` : `Add a ${meta.parentLabel.toLowerCase()}`}
              />
              <div className="flex shrink-0 gap-2">
                <Button
                  onClick={editingParent ? handleEditParent : handleAddParent}
                  isLoading={savingParent}
                  variant={editingParent ? 'primary' : 'success'}
                  icon={editingParent ? <FaPencilAlt className="h-3 w-3" /> : <FaPlus className="h-3 w-3" />}
                >
                  {editingParent ? 'Update' : 'Add'}
                </Button>
                {editingParent && (
                  <Button variant="outline" onClick={cancelParentEditing} icon={<FaTimes className="h-3 w-3" />}>
                    Cancel
                  </Button>
                )}
              </div>
            </div>
            {editingParent && (
              <p className="text-xs text-slate-500">
                Renaming <span className="font-semibold text-slate-700">{editingParent.parent}</span>
              </p>
            )}
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${meta.parentLabel.toLowerCase()}...`}
              leadingIcon={<FaSearch className="h-3.5 w-3.5" />}
            />
          </div>

          {loading ? (
            <ListSkeleton />
          ) : filteredParents.length === 0 ? (
            <EmptyState
              icon={<FaSitemap className="h-4 w-4" />}
              title={search ? 'Nothing matches your search' : `No ${meta.parentLabel.toLowerCase()} yet`}
              hint={search ? undefined : 'Add the first one above.'}
            />
          ) : (
            <ul className="max-h-[520px] space-y-1 overflow-y-auto p-2">
              {filteredParents.map((row) => {
                const isActive = selectedParent === row.parent;
                return (
                  <li key={row._id || row.parent}>
                    <div
                      onClick={() => {
                        if (editingParent) cancelParentEditing();
                        cancelChildEditing();
                        setSelectedParent(row.parent);
                      }}
                      className={`group flex cursor-pointer items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-sm transition ${
                        isActive
                          ? 'bg-sky-600 font-semibold text-white shadow-sm shadow-sky-600/25'
                          : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <span className="min-w-0 flex-1 truncate">{row.parent}</span>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                          isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                        }`}
                      >
                        {row.children?.length || 0}
                      </span>
                      <div className="flex shrink-0 gap-1 opacity-0 transition group-hover:opacity-100">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingParent(row);
                            setParentInput(row.parent);
                            setSelectedParent(row.parent);
                          }}
                          title="Rename"
                          className={`rounded-lg p-1.5 transition ${
                            isActive ? 'text-white hover:bg-white/20' : 'text-slate-400 hover:bg-sky-50 hover:text-sky-600'
                          }`}
                        >
                          <FaPencilAlt className="h-3 w-3" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget({ type: 'parent', value: row.parent });
                          }}
                          title="Delete"
                          className={`rounded-lg p-1.5 transition ${
                            isActive ? 'text-white hover:bg-white/20' : 'text-slate-400 hover:bg-rose-50 hover:text-rose-600'
                          }`}
                        >
                          <FaTrash className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        {/* ---- Children of the selected parent ---- */}
        <Panel
          title={selectedParent ? `${meta.childLabel} — ${selectedParent}` : meta.childLabel}
          subtitle={
            selectedParent
              ? `Offered whenever "${selectedParent}" is the selected ${meta.parentLabel.toLowerCase()}`
              : `Select a ${meta.parentLabel.toLowerCase()} to manage its ${meta.childLabel.toLowerCase()}`
          }
          icon={<FaLayerGroup className="h-3.5 w-3.5 text-sky-600" />}
          action={selectedParent ? <Badge variant="primary">{selected?.children?.length || 0} options</Badge> : null}
          className="min-h-[420px]"
        >
          {!selectedParent ? (
            <EmptyState
              icon={<FaSitemap className="h-4 w-4" />}
              title={`No ${meta.parentLabel.toLowerCase()} selected`}
              hint={`Pick one on the left to add or remove its ${meta.childLabel.toLowerCase()}.`}
            />
          ) : (
            <>
              <div className="flex flex-col gap-2 border-b border-slate-100 bg-slate-50/50 p-3 sm:flex-row">
                <Input
                  value={childInput}
                  onChange={(e) => setChildInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddChild()}
                  placeholder={`Add a ${meta.childLabel.toLowerCase()} under ${selectedParent}`}
                />
                <Button onClick={handleAddChild} isLoading={savingChild} icon={<FaPlus className="h-3 w-3" />} className="shrink-0">
                  Add
                </Button>
              </div>

              {!selected?.children?.length ? (
                <EmptyState
                  icon={<FaPlus className="h-4 w-4" />}
                  title={`No ${meta.childLabel.toLowerCase()} yet`}
                  hint={`Until one is added, forms fall back to the full ${meta.childLabel.toLowerCase()} list.`}
                />
              ) : (
                <ul className="max-h-[480px] divide-y divide-slate-100 overflow-y-auto">
                  {selected.children.map((child, index) => (
                    <li
                      key={`${child}-${index}`}
                      className="group flex items-center justify-between gap-3 px-4 py-2.5 transition hover:bg-slate-50"
                    >
                      {editingChild === child ? (
                        <>
                          <Input
                            value={editingChildText}
                            autoFocus
                            onChange={(e) => setEditingChildText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleEditChild();
                              if (e.key === 'Escape') cancelChildEditing();
                            }}
                          />
                          <div className="flex shrink-0 gap-2">
                            <Button size="sm" onClick={handleEditChild} isLoading={savingChild}>Save</Button>
                            <Button size="sm" variant="outline" onClick={cancelChildEditing}>Cancel</Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <span className="truncate text-sm text-slate-700">{child}</span>
                          <div className="flex shrink-0 gap-1 opacity-0 transition focus-within:opacity-100 group-hover:opacity-100">
                            <button
                              onClick={() => {
                                setEditingChild(child);
                                setEditingChildText(child);
                              }}
                              title="Rename"
                              className="rounded-lg p-2 text-slate-400 transition hover:bg-sky-50 hover:text-sky-600"
                            >
                              <FaPencilAlt className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => setDeleteTarget({ type: 'child', value: child })}
                              title="Delete"
                              className="rounded-lg p-2 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
                            >
                              <FaTrash className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </Panel>
      </div>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() =>
          deleteTarget?.type === 'parent'
            ? handleDeleteParent(deleteTarget.value)
            : handleDeleteChild(deleteTarget.value)
        }
        isLoading={deleting}
        title={deleteTarget?.type === 'parent' ? `Delete ${meta.parentLabel.toLowerCase()}` : `Delete ${meta.childLabel.toLowerCase()}`}
        message={
          deleteTarget?.type === 'parent'
            ? `Delete "${deleteTarget?.value}" and every ${meta.childLabel.toLowerCase()} under it? This cannot be undone.`
            : `Remove "${deleteTarget?.value}" from ${selectedParent}?`
        }
      />
    </div>
  );
};

/* ---------------- Page shell ---------------- */

const TABS = [
  { id: 'lists', label: 'Dropdown Lists', icon: <FaListUl className="h-3.5 w-3.5" /> },
  { id: 'linked', label: 'Linked Lists', icon: <FaLayerGroup className="h-3.5 w-3.5" /> },
  { id: 'services', label: 'Services & Sub-Services', icon: <FaSitemap className="h-3.5 w-3.5" /> },
];

const DropDownOptions = () => {
  const [tab, setTab] = useState('lists');
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.dropDown);

  return (
    <div className="w-full space-y-6 p-4 md:p-8">
      <div className="flex flex-col gap-4 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold tracking-tight text-slate-800">
            <FaListUl className="text-sky-600" /> Dropdowns &amp; Services
          </h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Manage the option lists and service catalogue used across every form in the clinic
          </p>
        </div>
        {tab === 'lists' && (
          <Button
            variant="outline"
            size="sm"
            isLoading={loading}
            onClick={() => dispatch(getAllDropDowns())}
            icon={<FaSyncAlt className="h-3.5 w-3.5" />}
          >
            Refresh
          </Button>
        )}
      </div>

      <div className="inline-flex flex-wrap gap-1 rounded-xl border border-slate-200/80 bg-white p-1 shadow-sm">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition ${
              tab === t.id
                ? 'bg-sky-600 text-white shadow-sm shadow-sky-600/25'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'lists' && <DropDownOption />}
      {tab === 'linked' && <LinkedLists />}
      {tab === 'services' && <Services />}
    </div>
  );
};


export default DropDownOptions;
